/**
 * Configuración › Depósito (2026-09-10) — depósito con dos correos.
 *
 * Bloques: Correos (activar/desactivar, nombre, color, orden) · Envíos sin correo definido ·
 * Impresión (franja en la hoja, bandera de fondo) · Escaneo de bolsa obligatorio por vendedor.
 * Cada cambio guarda al instante. API: /api/settings/warehouse (permiso settings.warehouse).
 */
import { useCallback, useEffect, useState } from 'react';
import { Loader2, Check, AlertTriangle, ArrowUp, ArrowDown } from 'lucide-react';
import { api } from '../../../api/client';
import { useAuthStore, type UserPreferences } from '../../../stores/authStore';

export interface WhCourier {
  id: number;
  key: string;
  name: string;
  color: string;
  active: boolean;
  sort_order: number;
}
type WhMode = 'simple' | 'multi' | 'none';
interface WhConfig {
  unknown_courier_rule: string;
  print_courier_on_detail_sheet: boolean;
  flag_watermark: boolean;
}
interface SellerRow {
  id: number;
  nickname: string;
  require_bag_scan: boolean;
}
interface SettingsPayload {
  success: boolean;
  couriers: WhCourier[];
  mode: WhMode;
  config: WhConfig;
  sellers: SellerRow[];
  error?: string;
}

function Toggle({
  checked,
  onChange,
  disabled,
  label,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  label?: string;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      aria-label={label}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 shrink-0 items-center rounded-full transition-colors disabled:opacity-50 ${
        checked ? 'bg-emerald-500' : 'bg-gray-400'
      }`}
    >
      <span
        className={`inline-block h-5 w-5 transform rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-5' : 'translate-x-0.5'
        }`}
      />
    </button>
  );
}

function ModeBadge({ mode }: { mode: WhMode }) {
  const text =
    mode === 'multi'
      ? 'Modo dos correos: una bolsa y un documento de entrega por correo'
      : mode === 'simple'
        ? 'Modo un solo correo: una bolsa y un documento, como antes'
        : 'Sin correos activos: no se puede abrir bolsas';
  const cls =
    mode === 'multi'
      ? 'bg-emerald-100 text-emerald-800 dark:bg-emerald-900/30 dark:text-emerald-200'
      : mode === 'simple'
        ? 'bg-blue-100 text-blue-800 dark:bg-blue-900/30 dark:text-blue-200'
        : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-200';
  return <div className={`rounded-lg px-3 py-2 text-sm font-medium ${cls}`}>{text}</div>;
}

export function WarehouseTab() {
  const [data, setData] = useState<SettingsPayload | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);
  const [savedFlash, setSavedFlash] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [nameDraft, setNameDraft] = useState<Record<number, string>>({});
  // Preferencia PERSONAL (ver §"Mis preferencias" abajo): no viaja con la configuración del
  // depósito, que es de toda la empresa. Vive en la sesión y se guarda por usuario.
  const whPopupMessages = useAuthStore((s) => s.user?.preferences?.wh_popup_messages === true);
  const setPreferences = useAuthStore((s) => s.setPreferences);

  const load = useCallback(async () => {
    setLoading(true);
    const res = (await api.get<unknown>('/settings/warehouse')) as unknown as SettingsPayload;
    if (res.success) {
      setData(res);
      const drafts: Record<number, string> = {};
      for (const c of res.couriers) drafts[c.id] = c.name;
      setNameDraft(drafts);
      setError(null);
    } else {
      setError(res.error || 'No se pudo cargar la configuración del depósito');
    }
    setLoading(false);
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  function flash(key: string) {
    setSavedFlash(key);
    setTimeout(() => setSavedFlash((k) => (k === key ? null : k)), 1500);
  }

  async function saveCourier(id: number, patch: Partial<WhCourier>) {
    setSaving(`courier-${id}`);
    setError(null);
    const res = (await api.put<unknown>(`/settings/warehouse/couriers/${id}`, patch)) as unknown as {
      success: boolean;
      error?: string;
      details?: { open_bags?: number; pending_items?: number } | null;
    };
    if (!res.success) {
      const d = res.details;
      setError(
        d && (d.open_bags || d.pending_items)
          ? `${res.error} Cerrá las bolsas y liberá los lotes de ese correo antes de desactivarlo.`
          : res.error || 'No se pudo guardar'
      );
    } else {
      flash(`courier-${id}`);
    }
    await load();
    setSaving(null);
  }

  async function saveConfig(patch: Partial<WhConfig>) {
    setSaving('config');
    setError(null);
    const res = (await api.put<unknown>('/settings/warehouse/config', patch)) as unknown as {
      success: boolean;
      error?: string;
    };
    if (!res.success) setError(res.error || 'No se pudo guardar');
    else flash('config');
    await load();
    setSaving(null);
  }

  async function saveRequireBag(sellerId: number, value: boolean) {
    setSaving(`seller-${sellerId}`);
    setError(null);
    const res = (await api.put<unknown>(`/settings/warehouse/sellers/${sellerId}/require-bag-scan`, {
      value,
    })) as unknown as { success: boolean; error?: string };
    if (!res.success) setError(res.error || 'No se pudo guardar');
    else flash(`seller-${sellerId}`);
    await load();
    setSaving(null);
  }

  /**
   * Guarda una preferencia del PROPIO usuario. El cliente `api` no lanza en 4xx: hay que mirar
   * `success`. Solo se refleja en la sesión lo que el servidor devolvió ya guardado.
   */
  async function savePreference(patch: UserPreferences) {
    setSaving('prefs');
    setError(null);
    const res = (await api.put<unknown>('/auth/me/preferences', patch)) as unknown as {
      success: boolean;
      error?: string;
      data?: { preferences?: UserPreferences };
    };
    if (!res.success) {
      setError(res.error || 'No se pudo guardar tu preferencia');
    } else {
      setPreferences(res.data?.preferences ?? {});
      flash('prefs');
    }
    setSaving(null);
  }

  async function move(c: WhCourier, dir: -1 | 1) {
    if (!data) return;
    const sorted = [...data.couriers].sort((a, b) => a.sort_order - b.sort_order);
    const idx = sorted.findIndex((x) => x.id === c.id);
    const other = sorted[idx + dir];
    if (!other) return;
    setSaving(`courier-${c.id}`);
    await api.put(`/settings/warehouse/couriers/${c.id}`, { sort_order: other.sort_order });
    await api.put(`/settings/warehouse/couriers/${other.id}`, { sort_order: c.sort_order });
    await load();
    setSaving(null);
  }

  if (loading && !data) {
    return (
      <div className="flex items-center gap-2 text-[var(--color-text-secondary)]">
        <Loader2 size={16} className="animate-spin" /> Cargando…
      </div>
    );
  }
  if (!data) {
    return <div className="text-sm text-red-600">{error || 'Sin datos'}</div>;
  }

  const couriers = [...data.couriers].sort((a, b) => a.sort_order - b.sort_order);
  const Saved = ({ k }: { k: string }) =>
    savedFlash === k ? (
      <span className="inline-flex items-center gap-1 text-xs text-emerald-600">
        <Check size={12} /> Guardado
      </span>
    ) : saving === k ? (
      <Loader2 size={12} className="animate-spin text-[var(--color-text-secondary)]" />
    ) : null;

  return (
    <div className="space-y-8">
      <div>
        <h2 className="text-lg font-semibold text-[var(--color-text)]">Depósito</h2>
        <p className="mt-1 text-sm text-[var(--color-text-secondary)]">
          Cómo trabaja la mesa del depósito con los correos que retiran los paquetes.
        </p>
      </div>

      {error && (
        <div className="flex items-start gap-2 rounded-lg border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-800 dark:border-red-800 dark:bg-red-900/20 dark:text-red-200">
          <AlertTriangle size={16} className="mt-0.5 shrink-0" />
          <span>{error}</span>
        </div>
      )}

      {/* ── Correos ── */}
      <section className="space-y-3">
        <h3 className="text-base font-semibold text-[var(--color-text)]">Correos</h3>
        <ModeBadge mode={data.mode} />
        <p className="text-sm text-[var(--color-text-secondary)]">
          Con 2 o más correos activos el depósito trabaja con una bolsa por correo y un documento de
          entrega por correo. Con uno solo, todo va a una bolsa y un documento, como antes. Un correo
          con bolsas abiertas o paquetes en lotes sin liberar no se puede desactivar.
        </p>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr className="text-left text-xs uppercase tracking-wider text-[var(--color-text-secondary)]">
                <th className="py-2 pr-3">Activo</th>
                <th className="py-2 pr-3">Nombre</th>
                <th className="py-2 pr-3">Color</th>
                <th className="py-2 pr-3">Código ML</th>
                <th className="py-2 pr-3">Orden</th>
                <th className="py-2"></th>
              </tr>
            </thead>
            <tbody>
              {couriers.map((c, i) => (
                <tr key={c.id} className="border-t border-[var(--color-border)]">
                  <td className="py-2 pr-3">
                    <Toggle
                      checked={c.active}
                      disabled={saving !== null}
                      label={`Activar ${c.name}`}
                      onChange={(v) => saveCourier(c.id, { active: v })}
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <input
                      value={nameDraft[c.id] ?? c.name}
                      onChange={(e) => setNameDraft((d) => ({ ...d, [c.id]: e.target.value }))}
                      onBlur={() => {
                        const v = (nameDraft[c.id] ?? '').trim();
                        if (v && v !== c.name) saveCourier(c.id, { name: v });
                      }}
                      className="w-44 rounded border border-[var(--color-border)] bg-[var(--color-bg)] px-2 py-1 text-[var(--color-text)]"
                    />
                  </td>
                  <td className="py-2 pr-3">
                    <input
                      type="color"
                      value={c.color}
                      onChange={(e) => saveCourier(c.id, { color: e.target.value })}
                      className="h-8 w-12 cursor-pointer rounded border border-[var(--color-border)] bg-transparent"
                      aria-label={`Color de ${c.name}`}
                    />
                  </td>
                  <td className="py-2 pr-3 font-mono text-xs text-[var(--color-text-secondary)]">
                    {c.key}
                  </td>
                  <td className="py-2 pr-3">
                    <div className="flex items-center gap-1">
                      <button
                        type="button"
                        disabled={i === 0 || saving !== null}
                        onClick={() => move(c, -1)}
                        className="rounded border border-[var(--color-border)] p-1 disabled:opacity-30"
                        aria-label="Subir"
                      >
                        <ArrowUp size={14} />
                      </button>
                      <button
                        type="button"
                        disabled={i === couriers.length - 1 || saving !== null}
                        onClick={() => move(c, 1)}
                        className="rounded border border-[var(--color-border)] p-1 disabled:opacity-30"
                        aria-label="Bajar"
                      >
                        <ArrowDown size={14} />
                      </button>
                    </div>
                  </td>
                  <td className="py-2">
                    <Saved k={`courier-${c.id}`} />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </section>

      {/* ── Envíos sin correo definido ── */}
      <section className="space-y-3">
        <h3 className="text-base font-semibold text-[var(--color-text)]">
          Envíos sin correo definido
        </h3>
        <p className="text-sm text-[var(--color-text-secondary)]">
          Qué hacer cuando Mercado Libre no informa el correo de un envío (pasa muy pocas veces,
          casi siempre en envíos cancelados).
        </p>
        <div className="space-y-2">
          <label className="flex items-center gap-2 text-sm text-[var(--color-text)]">
            <input
              type="radio"
              name="unknown_rule"
              checked={data.config.unknown_courier_rule === 'ask'}
              onChange={() => saveConfig({ unknown_courier_rule: 'ask' })}
            />
            Preguntar al operario en el popup
          </label>
          {couriers
            .filter((c) => c.active)
            .map((c) => (
              <label key={c.id} className="flex items-center gap-2 text-sm text-[var(--color-text)]">
                <input
                  type="radio"
                  name="unknown_rule"
                  checked={data.config.unknown_courier_rule === c.key}
                  onChange={() => saveConfig({ unknown_courier_rule: c.key })}
                />
                Asignar a{' '}
                <span className="font-medium" style={{ color: c.color }}>
                  {c.name}
                </span>
              </label>
            ))}
          <Saved k="config" />
        </div>
      </section>

      {/* ── Impresión ── */}
      <section className="space-y-3">
        <h3 className="text-base font-semibold text-[var(--color-text)]">Impresión y pantalla</h3>
        <div className="flex items-center justify-between gap-4 rounded-lg border border-[var(--color-border)] px-3 py-2">
          <div>
            <div className="text-sm font-medium text-[var(--color-text)]">
              Imprimir correo y bolsa en la hoja de detalle
            </div>
            <div className="text-xs text-[var(--color-text-secondary)]">
              Franja tipo "CHILEXPRESS · BOLSA 0001" debajo del encabezado de cada hoja.
            </div>
          </div>
          <Toggle
            checked={data.config.print_courier_on_detail_sheet}
            disabled={saving !== null}
            label="Imprimir correo y bolsa"
            onChange={(v) => saveConfig({ print_courier_on_detail_sheet: v })}
          />
        </div>
        <div className="flex items-center justify-between gap-4 rounded-lg border border-[var(--color-border)] px-3 py-2">
          <div>
            <div className="text-sm font-medium text-[var(--color-text)]">
              Bandera del país de destino de fondo en el popup de escaneo
            </div>
            <div className="text-xs text-[var(--color-text-secondary)]">
              Marca de agua muy suave con la bandera del país al que va la venta.
            </div>
          </div>
          <Toggle
            checked={data.config.flag_watermark}
            disabled={saving !== null}
            label="Bandera de fondo"
            onChange={(v) => saveConfig({ flag_watermark: v })}
          />
        </div>
      </section>

      {/* ── Mis preferencias (por usuario, no de la empresa) ── */}
      <section className="space-y-3">
        <h3 className="text-base font-semibold text-[var(--color-text)]">Mis preferencias</h3>
        <p className="text-sm text-[var(--color-text-secondary)]">
          Estos ajustes son tuyos: cambian solo lo que ves vos y te siguen a cualquier computadora
          con la que entres. No afectan a los demás usuarios.
        </p>
        <div className="flex items-center justify-between gap-4 rounded-lg border border-[var(--color-border)] px-3 py-2">
          <div>
            <div className="text-sm font-medium text-[var(--color-text)]">
              Ver mensajes en pop-up de cierre de orden
            </div>
            <div className="text-xs text-[var(--color-text-secondary)]">
              Agrega un bloque plegable con la conversación del comprador dentro de la ventana que
              se abre al escanear. Se guarda para tu usuario.
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Saved k="prefs" />
            <Toggle
              checked={whPopupMessages}
              disabled={saving !== null}
              label="Ver mensajes en el pop-up de cierre de orden"
              onChange={(v) => savePreference({ wh_popup_messages: v })}
            />
          </div>
        </div>
      </section>

      {/* ── Escaneo de bolsa obligatorio ── */}
      <section className="space-y-3">
        <h3 className="text-base font-semibold text-[var(--color-text)]">
          Escaneo de bolsa obligatorio
        </h3>
        <p className="text-sm text-[var(--color-text-secondary)]">
          Si está activado, no se puede agregar un paquete de ese vendedor sin una bolsa abierta.
          Con dos correos activos la bolsa del correo es obligatoria siempre, más allá de este
          interruptor.
        </p>
        <div className="space-y-2">
          {data.sellers.map((s) => (
            <div
              key={s.id}
              className="flex items-center justify-between gap-4 rounded-lg border border-[var(--color-border)] px-3 py-2"
            >
              <div className="text-sm text-[var(--color-text)]">{s.nickname}</div>
              <div className="flex items-center gap-2">
                <Saved k={`seller-${s.id}`} />
                <Toggle
                  checked={s.require_bag_scan}
                  disabled={saving !== null}
                  label={`Bolsa obligatoria para ${s.nickname}`}
                  onChange={(v) => saveRequireBag(s.id, v)}
                />
              </div>
            </div>
          ))}
        </div>
      </section>
    </div>
  );
}
